import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Check, FileText, Headphones, ShoppingCart, Star, Zap } from "lucide-react";
import { api } from "../api";
import { BookCover } from "../components/BookCover";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { useCart } from "../context/CartContext";

// Prawdziwe ujęcia z działających aplikacji i gier (lekko upiększone)
const APP_SHOTS = {
  "digilib-world-matematyka": ["/img/screenshots/world-1.jpg", "/img/screenshots/world-2.jpg", "/img/screenshots/world-3.jpg", "/img/screenshots/world-4.jpg"],
  "digikids-matematyka": ["/img/screenshots/kids-1.jpg"],
  "digilingua-hiszpanski": ["/img/screenshots/learn-3.jpg", "/img/screenshots/learn-4.jpg", "/img/screenshots/learn-2.jpg", "/img/screenshots/learn-1.jpg"],
};

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      api.get(`/products?category=${r.data.category}`).then((res) => {
        setSimilar(res.data.filter((p) => p.id !== r.data.id).slice(0, 4));
      });
    }).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [id]);

  if (notFound) return <main className="mx-auto max-w-3xl px-4 py-40 text-center text-slate-400">Nie znaleziono produktu.</main>;
  if (!product) return <main className="mx-auto max-w-7xl px-4 py-40 text-center text-slate-500">Ładowanie...</main>;

  const discount = product.old_price ? Math.round((1 - product.price / product.old_price) * 100) : 0;

  return (
    <main className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6" data-testid="product-page">
      <Link to="/" className="flex w-fit items-center gap-2 text-sm text-slate-400 transition-colors hover:text-cyan-300">
        <ArrowLeft className="h-4 w-4" /> Wróć do katalogu
      </Link>

      <div className="mt-8 grid gap-10 lg:grid-cols-[320px_1fr]">
        <Reveal>
          <div className="mx-auto max-w-xs lg:mx-0">
            <BookCover product={product} className="shadow-[0_30px_80px_-20px_rgba(6,182,212,0.4)]" />
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div>
            <span className="inline-flex items-center gap-2 rounded-full glass-panel px-4 py-1.5 text-xs font-semibold text-cyan-300">
              {product.format === "audiobook" ? <Headphones className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />}
              {product.format === "audiobook" ? "Audiobook MP3" : "Ebook EPUB/PDF/MOBI"}
            </span>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{product.title}</h1>
            <p className="mt-2 text-lg text-slate-400">{product.author}</p>

            <div className="mt-4 flex items-center gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
              ))}
              <span className="text-sm text-slate-400">{product.rating} · {product.reviews} opinii</span>
            </div>

            <p className="mt-6 max-w-2xl leading-relaxed text-slate-300">{product.description}</p>

            <div className="mt-6 grid max-w-md grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl glass-panel p-3.5"><span className="text-slate-500">Wydawca</span><div className="mt-1 font-semibold text-slate-200">{product.publisher}</div></div>
              <div className="rounded-xl glass-panel p-3.5"><span className="text-slate-500">Rok wydania</span><div className="mt-1 font-semibold text-slate-200">{product.year}</div></div>
              <div className="rounded-xl glass-panel p-3.5"><span className="text-slate-500">{product.format === "audiobook" ? "Długość" : "Liczba stron"}</span><div className="mt-1 font-semibold text-slate-200">{product.duration || product.pages}</div></div>
              <div className="rounded-xl glass-panel p-3.5"><span className="text-slate-500">ISBN</span><div className="mt-1 font-semibold text-slate-200">{product.isbn}</div></div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-5">
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-4xl font-extrabold text-white">{product.price.toFixed(2)} zł</span>
                  {product.old_price && <span className="text-lg text-slate-500 line-through">{product.old_price.toFixed(2)} zł</span>}
                </div>
                {discount > 0 && <span className="mt-1 inline-block rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2.5 py-0.5 text-xs font-bold text-emerald-300">Oszczędzasz {discount}%</span>}
              </div>
              <button
                data-testid="product-add-to-cart"
                onClick={() => add(product)}
                className="flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 px-8 py-3.5 font-display text-sm font-bold text-slate-950 shadow-xl shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
              >
                <ShoppingCart className="h-4 w-4" /> Dodaj do koszyka
              </button>
            </div>

            <div className="mt-8 space-y-2 text-sm text-slate-400">
              <div className="flex items-center gap-2.5"><Zap className="h-4 w-4 text-cyan-400" /> Natychmiastowa dostawa na email po opłaceniu</div>
              <div className="flex items-center gap-2.5"><FileText className="h-4 w-4 text-cyan-400" /> Wszystkie formaty w cenie — EPUB, PDF, MOBI / MP3</div>
              <div className="flex items-center gap-2.5"><Check className="h-4 w-4 text-cyan-400" /> Płatności: Visa, Mastercard, Maestro, BLIK, przelew</div>
            </div>
          </div>
        </Reveal>
      </div>

      {APP_SHOTS[product.id] && (
        <section className="mt-16" data-testid="product-shots">
          <h2 className="font-display text-2xl font-bold tracking-tight">Zobacz, jak wygląda <span className="gradient-text">w środku</span></h2>
          <div className={`mt-6 grid gap-4 ${APP_SHOTS[product.id].length >= 4 ? "grid-cols-2 lg:grid-cols-4" : APP_SHOTS[product.id].length > 1 ? "sm:grid-cols-2" : ""}`}>
            {APP_SHOTS[product.id].map((src, i) => (
              <Reveal key={src} delay={i * 90}>
                <div className="glow-border group overflow-hidden rounded-3xl p-[2px]">
                  <img
                    src={src}
                    alt={`${product.title} — podgląd ${i + 1}`}
                    loading="lazy"
                    className="w-full rounded-3xl transition-all duration-700 group-hover:scale-[1.03] group-hover:saturate-150"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {similar.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-2xl font-bold tracking-tight">Podobne <span className="gradient-text">tytuły</span></h2>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
            {similar.map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
